/**
 * Wind Visual Effects — horizontal streaks + ambient dust particles.
 * Higher fidelity than original: head-to-tail fade, width/color variation, soft dust layer.
 * Sakurai: contrast (crisp lines + soft circles), display priority (below tanks).
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD, WIND, Z } from '../constants';

interface WindStreak {
  x: number;
  y: number;
  length: number;
  speed: number;
  alpha: number;
  width: number;       // 1 or 2 px (depth variation)
  color: number;       // 0xffffff or 0xccddff
  vy: number;          // subtle vertical drift
  direction: 1 | -1;   // matches wind sign
}

interface WindDust {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  alpha: number;
  life: number;
  maxLife: number;
}

const V = WIND.VISUAL;

export class WindRenderer {
  private streakGfx: Graphics;
  private dustGfx: Graphics;
  private streaks: WindStreak[] = [];
  private dust: WindDust[] = [];

  constructor(worldContainer: Container) {
    // Dust below streaks for depth layering
    this.dustGfx = new Graphics();
    this.dustGfx.zIndex = Z.WIND_EFFECTS - 0.1;
    worldContainer.addChild(this.dustGfx);

    this.streakGfx = new Graphics();
    this.streakGfx.zIndex = Z.WIND_EFFECTS;
    worldContainer.addChild(this.streakGfx);
  }

  update(dt: number, wind: number): void {
    const absWind = Math.abs(wind);

    // No effects when calm
    if (absWind < 0.005) {
      this.streaks.length = 0;
      this.dust.length = 0;
      this.streakGfx.clear();
      this.dustGfx.clear();
      return;
    }

    this.updateStreaks(dt, wind, absWind);
    this.updateDust(dt, wind, absWind);
    this.draw();
  }

  // --- Streaks ---

  private updateStreaks(dt: number, wind: number, absWind: number): void {
    // Spawn
    const spawnChance = V.SPAWN_RATE * absWind * 10;
    if (this.streaks.length < V.MAX_STREAKS && Math.random() < spawnChance) {
      this.streaks.push(this.createStreak(wind, absWind));
    }

    // Move & cull — use visible width so streaks fill gutters on wide screens
    const visW = WORLD.VISIBLE_WIDTH;
    const visOff = (WORLD.VIRTUAL_WIDTH - visW) / 2;
    for (let i = this.streaks.length - 1; i >= 0; i--) {
      const s = this.streaks[i];
      s.x += s.speed * s.direction * dt;
      s.y += s.vy * dt;

      // Off-screen on downwind side?
      const dead = s.direction > 0
        ? s.x - s.length > visOff + visW
        : s.x + s.length < visOff;
      if (dead) {
        this.streaks[i] = this.streaks[this.streaks.length - 1];
        this.streaks.pop();
      }
    }
  }

  private createStreak(wind: number, absWind: number): WindStreak {
    const lengthScale = 0.5 + absWind * 8;
    const length = (V.LENGTH_MIN + Math.random() * (V.LENGTH_MAX - V.LENGTH_MIN)) * lengthScale;
    const direction: 1 | -1 = wind > 0 ? 1 : -1;

    // Spawn at upwind edge — use visible width to cover gutters on wide screens
    const visW = WORLD.VISIBLE_WIDTH;
    const visOff = (WORLD.VIRTUAL_WIDTH - visW) / 2;
    return {
      x: direction > 0 ? visOff - length : visOff + visW + length,
      y: 50 + Math.random() * (WORLD.VIRTUAL_HEIGHT * 0.85 - 50),
      length,
      speed: V.BASE_SPEED * absWind * 10 * (0.8 + Math.random() * 0.4),
      alpha: V.ALPHA_MIN + Math.random() * (V.ALPHA_MAX - V.ALPHA_MIN),
      width: Math.random() < 0.8 ? 1 : 2,
      color: Math.random() < 0.85 ? 0xffffff : 0xccddff,
      vy: (Math.random() * 2 - 1) * V.DRIFT_RANGE,
      direction,
    };
  }

  // --- Dust ---

  private updateDust(dt: number, wind: number, absWind: number): void {
    // Spawn
    const spawnChance = V.DUST_SPAWN_RATE * absWind * 10;
    if (this.dust.length < V.MAX_DUST && Math.random() < spawnChance) {
      this.dust.push(this.createDust(wind));
    }

    // Move & cull — use visible width so dust fills gutters on wide screens
    const visW = WORLD.VISIBLE_WIDTH;
    const visOff = (WORLD.VIRTUAL_WIDTH - visW) / 2;
    for (let i = this.dust.length - 1; i >= 0; i--) {
      const d = this.dust[i];
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.life -= dt;

      if (d.life <= 0 || d.x < visOff - 50 || d.x > visOff + visW + 50) {
        this.dust[i] = this.dust[this.dust.length - 1];
        this.dust.pop();
      }
    }
  }

  private createDust(wind: number): WindDust {
    const dv = WORLD.VISIBLE_WIDTH;
    const dOff = (WORLD.VIRTUAL_WIDTH - dv) / 2;
    const life = V.DUST_LIFE_MIN + Math.random() * (V.DUST_LIFE_MAX - V.DUST_LIFE_MIN);
    return {
      x: dOff + Math.random() * dv,
      y: 50 + Math.random() * (WORLD.VIRTUAL_HEIGHT * 0.85 - 50),
      vx: wind * V.DUST_SPEED * (0.6 + Math.random() * 0.8),
      vy: (Math.random() * 2 - 1) * 10,
      radius: 1 + Math.random() * 1.5,
      alpha: 0.12 + Math.random() * 0.13,
      life,
      maxLife: life,
    };
  }

  // --- Draw ---

  private draw(): void {
    // Streaks: clear and redraw each frame
    this.streakGfx.clear();
    for (const s of this.streaks) {
      const headX = s.x;
      const tailX = s.x - s.direction * s.length;
      const midX = (headX + tailX) / 2;

      // Leading half — brighter
      this.streakGfx.moveTo(headX, s.y);
      this.streakGfx.lineTo(midX, s.y);
      this.streakGfx.stroke({ width: s.width, color: s.color, alpha: s.alpha });

      // Trailing half — dimmer (40% of leading)
      this.streakGfx.moveTo(midX, s.y);
      this.streakGfx.lineTo(tailX, s.y);
      this.streakGfx.stroke({ width: s.width, color: s.color, alpha: s.alpha * 0.4 });
    }

    // Dust: clear and redraw each frame
    this.dustGfx.clear();
    for (const d of this.dust) {
      // Fade out over last 30% of life
      const fadeThreshold = d.maxLife * 0.3;
      const fade = d.life < fadeThreshold ? d.life / fadeThreshold : 1;

      this.dustGfx.circle(d.x, d.y, d.radius);
      this.dustGfx.fill({ color: V.DUST_COLOR, alpha: d.alpha * fade });
    }
  }

  destroy(): void {
    this.streakGfx.destroy();
    this.dustGfx.destroy();
  }
}
